// =============================================================================
// src/lib/api.ts  –  Typed HTTP client for the P2P backend
// =============================================================================

import { logDebug } from './debug';

const BASE = import.meta.env.VITE_API_URL ?? '';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const url = `${BASE}${path}`;
  const startedAt = Date.now();
  const bodyPreview = typeof init?.body === 'string' ? init.body.slice(0, 300) : undefined;

  logDebug('api', 'request.start', { method, path, url, bodyPreview });
  try {
    const res = await fetch(url, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
    const data = await res.json();
    const durationMs = Date.now() - startedAt;
    logDebug('api', 'request.done', {
      method,
      path,
      status: res.status,
      ok: res.ok,
      durationMs,
      code: data?.code,
      error: data?.error,
    }, res.ok ? 'info' : 'warn');
    if (!res.ok) throw Object.assign(new Error(data.error ?? 'API error'), { code: data.code, status: res.status });
    return data as T;
  } catch (err) {
    logDebug('api', 'request.error', {
      method,
      path,
      durationMs: Date.now() - startedAt,
      error: err,
    }, 'error');
    throw err;
  }
}

// ── Room ─────────────────────────────────────────────────────────────────────
export interface CreateRoomBody {
  type: 'quick' | 'private' | 'drop' | 'broadcast';
  password?: string;
  hostNickname: string;
  hostPublicKey: string;
  deviceType: string;
  ttlSec?: number;
}
export interface CreateRoomResult {
  roomId:    string;
  code:      string;
  inviteUrl: string;
  expiresAt: number;
  hostToken: string;  // server-signed proof of room ownership — present on join-direct to get host role
}
export const createRoom = (body: CreateRoomBody) =>
  req<CreateRoomResult>('/api/room/create', { method: 'POST', body: JSON.stringify(body) });

export interface JoinRoomBody {
  code: string;
  password?: string;
  nickname: string;
  publicKey: string;
  deviceType: string;
  peerId?: string;
}
export interface JoinRoomResult {
  roomId: string;
  wsUrl: string;
  peerId: string;
  expiresAt: number;
}
export const joinRoom = (body: JoinRoomBody) =>
  req<JoinRoomResult>('/api/room/join', { method: 'POST', body: JSON.stringify(body) });

export interface RoomInfo {
  roomId: string;
  type: string;
  memberCount: number;
  passwordProtected: boolean;
  expiresAt: number;
  transferCount: number;
}
export const getRoomInfo = (roomId: string) =>
  req<RoomInfo>(`/api/room/${roomId}/info`);

export const verifyRoomPassword = (roomId: string, password: string) =>
  req<{ ok: boolean }>(`/api/room/${roomId}/verify`, {
    method: 'POST', body: JSON.stringify({ password }),
  });

// Join a room directly by roomId (no code — used for LAN auto-join and host self-join)
export interface JoinDirectBody {
  roomId:     string;
  nickname:   string;
  publicKey:  string;
  deviceType: string;
  peerId?:    string;
  role?:      'host' | 'guest';    // only honoured if hostToken is also valid
  hostToken?: string;              // required to join as host — returned from createRoom
}
export const joinRoomDirect = (body: JoinDirectBody) =>
  req<JoinRoomResult>('/api/room/join-direct', { method: 'POST', body: JSON.stringify(body) });

// ── TURN ─────────────────────────────────────────────────────────────────────
export interface TurnCreds {
  iceServers: RTCIceServer[];
}
export const getTurnCreds = (peerId: string) =>
  req<TurnCreds>('/api/turn/credentials', { method: 'POST', body: JSON.stringify({ peerId }) });

// ── Invite ───────────────────────────────────────────────────────────────────
export const resolveInvite = (token: string, sig: string) =>
  req<{ roomId: string }>(`/api/invite/${token}?sig=${encodeURIComponent(sig)}`);

// ── LAN discovery ─────────────────────────────────────────────────────────────
export interface LanPeer {
  peerId: string;
  nickname: string;
  deviceType: string;
  lanRoomId?: string;
  lastSeen: number;
}
export const lanHeartbeat = (body: { peerId: string; nickname: string; deviceType: string; lanRoomId?: string }) =>
  req<{ subnetKey: string }>('/api/lan/heartbeat', { method: 'POST', body: JSON.stringify(body) });

export const getLanPeers = (peerId: string) =>
  req<{ peers: LanPeer[] }>(`/api/lan/peers?peerId=${encodeURIComponent(peerId)}`);

export const lanLeave = (peerId: string) =>
  req<{ ok: boolean }>('/api/lan/leave', { method: 'POST', body: JSON.stringify({ peerId }) });
